import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getSettings,
  setSettings as saveSettings,
  clearSaved,
  clearRecent,
  getLocalStorageUsageBytes,
  getLocalStorageUsagePct,
  getStorageLimits,
  exportAllLocalData,
  checkStorageWarning,
  getPlanningFactors,
  setPlanningFactor,
  resetPlanningFactor,
  resetAllPlanningFactors,
} from "@/lib/storage";
import { SafetyBanner } from "@/components/SafetyBanner";
import {
  Trash2,
  BookOpen,
  CreditCard,
  Activity,
  Download,
  HardDrive,
  Wifi,
  WifiOff,
  RefreshCw,
  AlertTriangle,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { APP_VERSION } from "@/lib/buildInfo";
import {
  getEngineeringDefaults, setEngineeringDefault, ENGINEERING_DEFAULT_KEYS,
} from "@/lib/storage";
import { WELL_CONTROL_STANDARDS } from "@/lib/killsheet/schema";
import {
  isSimulatedOffline,
  setSimulatedOffline,
  subscribe as subscribeConnectivity,
  getConnectivityState,
} from "@/lib/connectivity";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { Smartphone, Share } from "lucide-react";
import { IosInstallSteps } from "@/components/IosInstallSteps";

function formatBytes(bytes) {
  if (!bytes || bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function todayStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

// RC-1 — Engineering Defaults card.
//
// Three operating defaults that flow into every RCE consumer (Kill Sheet,
// migrated reports, calculators with settings.* bindings). The resolver
// has been publishing these since RCE-4.5; until now there was no UI to
// edit them. Values are persisted via the existing `setEngineeringDefault`
// helper (single source of truth — no parallel storage).
function EngineeringDefaultsCard() {
  const [vals, setVals] = useState(() => getEngineeringDefaults());

  const save = (key, value) => {
    try {
      const next = setEngineeringDefault(key, value);
      setVals(next);
    } catch (e) {
      toast.error(e.message || "Could not save engineering default");
    }
  };

  // Sanity assert — the factory key list must match the canonical export.
  // Catches accidental drift if anyone adds a new default without exposing it.
  if (ENGINEERING_DEFAULT_KEYS.length !== Object.keys(vals).length) {
    // Soft warning only — extra keys in storage shouldn't break the UI.
    console.warn("[Engineering Defaults] key drift detected");
  }

  return (
    <section className="card-rs p-4" data-testid="settings-engineering-defaults">
      <div className="label-up text-[var(--rs-text-3)] mb-3">
        Engineering Defaults · RCE
      </div>
      <p className="text-[11px] text-[var(--rs-text-3)] mb-3 leading-snug">
        These defaults flow into Kill Sheets, operational reports and any
        calculator that consumes <code className="text-[var(--rs-amber)]">settings.*</code>{" "}
        through the Engineering Context Engine. Override anywhere — these
        are the starting values.
      </p>

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-semibold mb-1.5"
                 htmlFor="settings-eng-safety-margin">
            Safety Margin (ppg)
          </label>
          <input
            id="settings-eng-safety-margin"
            data-testid="settings-eng-safety-margin"
            className="input-rs h-10"
            type="number"
            step="0.1"
            min="0"
            placeholder="e.g. 0.3"
            value={vals.safetyMarginPpg}
            onChange={(e) => save("safetyMarginPpg", e.target.value)}
          />
          <div className="text-[10.5px] text-[var(--rs-text-3)] mt-1">
            Additional planning margin added to Kill Mud Weight.
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold mb-1.5"
                 htmlFor="settings-eng-schedule-strokes">
            Schedule Interval (strokes)
          </label>
          <input
            id="settings-eng-schedule-strokes"
            data-testid="settings-eng-schedule-strokes"
            className="input-rs h-10"
            type="number"
            step="10"
            min="10"
            placeholder="e.g. 100"
            value={vals.scheduleIntervalStrokes}
            onChange={(e) => save("scheduleIntervalStrokes", e.target.value)}
          />
          <div className="text-[10.5px] text-[var(--rs-text-3)] mt-1">
            Default stroke increment for the driller&apos;s schedule grid.
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold mb-1.5"
                 htmlFor="settings-eng-wc-standard">
            Well Control Standard
          </label>
          <select
            id="settings-eng-wc-standard"
            data-testid="settings-eng-wc-standard"
            className="input-rs h-10"
            value={vals.wellControlStandard}
            onChange={(e) => save("wellControlStandard", e.target.value)}
          >
            {WELL_CONTROL_STANDARDS.map((std) => (
              <option key={std || "_none"} value={std}>
                {std || "— Not specified —"}
              </option>
            ))}
          </select>
          <div className="text-[10.5px] text-[var(--rs-text-3)] mt-1">
            Training / regulatory reference. Blank if not configured.
          </div>
        </div>
      </div>
    </section>
  );
}

// Persistent Install entry — Settings, unlike the Dashboard card, is never
// dismissed (it's a settings row, not a nag) and shows current state
// (Install button / iOS steps / already-installed) rather than hiding.
function InstallSettingsRow() {
  const { installed, canPromptInstall, showIOSInstructions, promptInstall } = usePwaInstall();
  const [iosStepsOpen, setIosStepsOpen] = useState(false);

  const onInstallClick = async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted") toast.success("RigSentinel installed");
  };

  return (
    <section className="card-rs p-4" data-testid="settings-install-section">
      <div className="label-up text-[var(--rs-text-3)] mb-3 inline-flex items-center gap-2">
        <Smartphone className="w-3.5 h-3.5" /> Install RigSentinel
      </div>

      {installed ? (
        <div className="text-xs text-[var(--rs-text-2)]" data-testid="settings-install-status-installed">
          RigSentinel is installed on this device.
        </div>
      ) : canPromptInstall ? (
        <>
          <p className="text-[11px] text-[var(--rs-text-3)] mb-3 leading-snug">
            Put the well at your fingertips — install RigSentinel for faster access and offline field use.
          </p>
          <button
            data-testid="settings-install-btn"
            onClick={onInstallClick}
            className="btn-amber w-full h-11 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up"
          >
            <Download className="w-4 h-4" /> Install RigSentinel
          </button>
        </>
      ) : showIOSInstructions ? (
        <>
          <button
            data-testid="settings-install-ios-toggle"
            onClick={() => setIosStepsOpen((v) => !v)}
            className="btn-ghost w-full h-11 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up"
          >
            <Share className="w-3.5 h-3.5" /> Install on iPhone/iPad
          </button>
          {iosStepsOpen && <IosInstallSteps testId="settings-install-ios-steps" />}
        </>
      ) : (
        <p className="text-[11px] text-[var(--rs-text-3)]" data-testid="settings-install-unavailable">
          Not available to install from this browser right now — check your browser menu for an &quot;Install app&quot; option.
        </p>
      )}
    </section>
  );
}

export default function Settings() {
  const [s, setS] = useState(getSettings());
  const navigate = useNavigate();
  const [usage, setUsage] = useState(() => ({
    bytes: getLocalStorageUsageBytes(),
    pct: getLocalStorageUsagePct(),
  }));
  const [versionTaps, setVersionTaps] = useState(0);
  const [devOptionsOpen, setDevOptionsOpen] = useState(false);
  const [simOffline, setSimOffline] = useState(isSimulatedOffline());
  const [conn, setConn] = useState(getConnectivityState());

  const update = (patch) => {
    const next = saveSettings(patch);
    setS(next);
  };

  // Recompute storage usage whenever the settings page is mounted and after
  // every clear/export action.
  const refreshUsage = () => {
    setUsage({
      bytes: getLocalStorageUsageBytes(),
      pct: getLocalStorageUsagePct(),
    });
  };

  useEffect(() => {
    const off = subscribeConnectivity(setConn);
    return off;
  }, []);

  const onVersionTap = () => {
    const next = versionTaps + 1;
    setVersionTaps(next);
    if (next >= 5 && !devOptionsOpen) {
      setDevOptionsOpen(true);
      toast.success("Developer Options unlocked");
    }
  };

  const onToggleSimOffline = () => {
    const nextVal = !simOffline;
    setSimulatedOffline(nextVal);
    setSimOffline(nextVal);
  };

  const onForceReloadSW = async () => {
    try {
      if (!("serviceWorker" in navigator)) {
        toast.error("Service worker not supported in this browser");
        return;
      }
      if (!window.confirm("Clear PWA cache and reload?\n\nThis evicts all cached app assets and reinstalls the service worker. Your saved sheets, well profiles, and reports are NOT touched (those live in localStorage).")) {
        return;
      }

      // 1) Ask the active SW to clear all caches via the CLEAR_CACHES message.
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg && reg.active) {
        await new Promise((resolve) => {
          try {
            const ch = new MessageChannel();
            ch.port1.onmessage = () => resolve();
            reg.active.postMessage({ type: "CLEAR_CACHES" }, [ch.port2]);
            setTimeout(resolve, 1500);   // safety net
          } catch { resolve(); }
        });
      }

      // 2) Belt + braces — also clear from the page side (in case the SW
      //    is unresponsive on a stuck install).
      if (typeof caches !== "undefined") {
        try {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        } catch { /* ignore */ }
      }

      // 3) Unregister every SW and reload — the next request fetches a
      //    fresh sw.js + fresh /static bundles from the network.
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));

      toast.success("Cache cleared — reloading.");
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error("Unable to clear cache: " + (e?.message || "unknown error"));
    }
  };

  // -------------------------------------------------------------------------
  // Dev: simulate the 4 MB localStorage warning by writing dummy data under a
  // dedicated prefix that the cleanup button can safely sweep. Writes ~50 KB
  // chunks until usage crosses the soft limit, then explicitly fires the
  // existing storage-warning toast so the user sees both the visual meter and
  // the toast in one tap. Used for iPhone-Safari validation.
  // -------------------------------------------------------------------------
  const SIM_PREFIX = "rsc.devSim.bloat.";

  const onSimulateStorageWarning = () => {
    try {
      const limits = getStorageLimits();
      const target = limits.softLimitBytes + 512 * 1024; // ~+0.5 MB headroom
      const chunk = "x".repeat(50000); // 50K chars ≈ 100 KB UTF-16
      let i = 0;
      while (getLocalStorageUsageBytes() < target && i < 200) {
        try {
          localStorage.setItem(`${SIM_PREFIX}${i}`, chunk);
        } catch (qe) {
          break; // browser quota hit before our soft target — still triggers warning
        }
        i += 1;
      }
      const result = checkStorageWarning();
      refreshUsage();
      if (result.warn) {
        toast.warning(result.message, {
          id: "rs-storage-warning",
          duration: 5000,
        });
      } else {
        toast.error("Could not inflate storage above 4 MB on this device.");
      }
    } catch (e) {
      toast.error("Storage simulation failed");
    }
  };

  const onClearSimulatedData = () => {
    try {
      let cleared = 0;
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(SIM_PREFIX)) keys.push(k);
      }
      keys.forEach((k) => {
        localStorage.removeItem(k);
        cleared += 1;
      });
      refreshUsage();
      toast.success(
        cleared
          ? `Cleared ${cleared} simulated record${cleared === 1 ? "" : "s"}.`
          : "No simulated data to clear."
      );
    } catch {
      toast.error("Unable to clear simulated data");
    }
  };

  const onExportAll = () => {
    try {
      const payload = exportAllLocalData();
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rigsentinel_backup_${todayStamp()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error("Export failed — please try again");
    }
  };

  const limits = getStorageLimits();
  const overSoftLimit = usage.bytes >= limits.softLimitBytes;
  const usageBarColor = overSoftLimit
    ? "var(--rs-amber)"
    : "rgba(148,163,184,0.65)";

  return (
    <div className="flex flex-col gap-5" data-testid="settings-page">
      <section>
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">Configuration</div>
        <h2 className="font-heading font-black text-3xl tracking-tight mt-1">Settings</h2>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5">Personalize defaults and manage local data.</p>
      </section>

      <section className="card-rs p-4">
        <div className="label-up text-[var(--rs-text-3)] mb-3">Defaults</div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1.5">Default Units</label>
            <div className="flex gap-2">
              <button
                data-testid="units-imperial"
                data-active={s.units === "imperial"}
                onClick={() => update({ units: "imperial" })}
                className="unit-pill flex-1"
              >Imperial</button>
              <button
                data-testid="units-metric"
                data-active={s.units === "metric"}
                onClick={() => update({ units: "metric" })}
                className="unit-pill flex-1"
              >Metric</button>
            </div>
            <div className="text-[11px] text-[var(--rs-text-3)] mt-1.5">
              Calculators compute in canonical imperial units (ppg, psi, bbl, ft). When Metric is selected, inputs accept metric values, outputs / saved sheets / PDF / CSV are all converted to metric.
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5">Default Well Name</label>
            <input
              data-testid="default-well"
              className="input-rs h-10"
              placeholder="e.g. ALPHA-12H"
              value={s.defaultWell}
              onChange={(e) => update({ defaultWell: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5">Default Rig Name</label>
            <input
              data-testid="default-rig"
              className="input-rs h-10"
              placeholder="e.g. Rig 27"
              value={s.defaultRig}
              onChange={(e) => update({ defaultRig: e.target.value })}
            />
          </div>
        </div>
      </section>

      {/* RC-1 — Engineering Defaults UI. Edits `rsc.settings.v1.engineeringDefaults`,
          which the RCE resolver has been reading since RCE-4.5 (these values
          flow into every consumer of `settings.*` paths). Before this card
          shipped, the only way to change them was via DevTools. */}
      <EngineeringDefaultsCard />

      <InstallSettingsRow />

      <section className="card-rs p-4">
        <div className="label-up text-[var(--rs-text-3)] mb-3">Library & Plans</div>
        <button
          data-testid="open-library"
          onClick={() => navigate("/library")}
          className="btn-ghost w-full h-11 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up mb-2"
        >
          <BookOpen className="w-4 h-4" /> Open Field Data Library
        </button>
        <button
          data-testid="open-plans"
          onClick={() => navigate("/pricing")}
          className="btn-ghost w-full h-11 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up"
        >
          <CreditCard className="w-4 h-4" /> View Subscription Plans
        </button>
        <button
          data-testid="open-diagnostic"
          onClick={() => navigate("/diagnostic")}
          className="btn-ghost w-full h-11 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up mt-2"
        >
          <Activity className="w-4 h-4" /> Run Calculator Diagnostic
        </button>
        <button
          data-testid="open-rce-migration"
          onClick={() => navigate("/migrate/projects")}
          className="btn-ghost w-full h-11 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up mt-2"
        >
          <Activity className="w-4 h-4" /> Project Profile Migration (RCE)
        </button>
      </section>

      {/* ===================================================================
          Default Planning Factors — Phase R2c. User-editable planning aids,
          NOT safety certifications. Values are display/storage-only and are
          never applied to any calculation output in R2c.
          =================================================================== */}
      <PlanningFactorsCard />

      {/* ===================================================================
          Storage — local-only usage meter + Export All Reports safety valve.
          All data is saved locally on your device. Internet is only required
          for sharing reports and syncing across devices.
          =================================================================== */}
      <section className="card-rs p-4" data-testid="settings-storage-section">
        <div className="label-up text-[var(--rs-text-3)] mb-3 inline-flex items-center gap-2">
          <HardDrive className="w-3.5 h-3.5" /> Storage
        </div>
        <div className="text-xs text-[var(--rs-text-2)] mb-2 flex items-center justify-between">
          <span data-testid="storage-usage-text">
            {formatBytes(usage.bytes)} used of ~{formatBytes(limits.hardLimitBytes)} ({usage.pct}%)
          </span>
          {overSoftLimit && (
            <span
              data-testid="storage-warning-pill"
              className="text-[10px] px-1.5 py-0.5 rounded font-mono-rs uppercase tracking-wider"
              style={{
                background: "rgba(255,179,0,0.18)",
                color: "var(--rs-amber)",
                border: "1px solid rgba(255,179,0,0.45)",
              }}
            >
              Nearly full
            </span>
          )}
        </div>
        <div
          className="h-1.5 w-full rounded overflow-hidden mb-3"
          style={{ background: "rgba(148,163,184,0.15)" }}
          aria-hidden="true"
        >
          <div
            className="h-full transition-all"
            style={{
              width: `${Math.max(2, usage.pct)}%`,
              background: usageBarColor,
            }}
          />
        </div>
        {overSoftLimit && (
          <p
            className="text-[11px] text-[var(--rs-amber)] mb-2 leading-snug"
            data-testid="storage-warning-text"
          >
            Local storage nearly full. Export or delete old reports.
          </p>
        )}
        <button
          data-testid="export-all-reports"
          onClick={onExportAll}
          className="btn-ghost w-full h-11 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up"
        >
          <Download className="w-4 h-4" /> Export All Reports
        </button>
        <p className="text-[11px] text-[var(--rs-text-3)] mt-2 leading-snug">
          All data is saved locally on your device. Internet is only required for sharing reports and syncing across devices.
        </p>
      </section>

      <section className="card-rs p-4">
        <div className="label-up text-[var(--rs-text-3)] mb-3">Local Data</div>
        <div className="grid grid-cols-1 gap-2">
          <button
            data-testid="clear-recent"
            onClick={() => { clearRecent(); toast.success("Recent list cleared"); refreshUsage(); }}
            className="btn-ghost h-10 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up"
          >
            <Trash2 className="w-3.5 h-3.5" /> Clear Recently Used
          </button>
          <button
            data-testid="clear-saved"
            onClick={() => {
              if (window.confirm("Delete all saved calculations on this device?")) {
                clearSaved(); toast.success("All saved sheets deleted"); refreshUsage();
              }
            }}
            className="btn-ghost h-10 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up"
            style={{ borderColor: "#C62828", color: "#FCA5A5" }}
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete All Saved Sheets
          </button>
        </div>
      </section>

      {/* About — version tap target unlocks Developer Options */}
      <section className="card-rs p-4" data-testid="settings-about-section">
        <div className="label-up text-[var(--rs-text-3)] mb-2">About</div>
        <div className="text-xs text-[var(--rs-text-2)]">
          <div><span className="text-[var(--rs-text-3)]">App:</span> RigSentinel</div>
          <div data-testid="settings-release-status"><span className="text-[var(--rs-text-3)]">Release status:</span> Soft Launch — actively developed; report issues via Settings support options.</div>
          <button
            type="button"
            data-testid="version-tap-target"
            onClick={onVersionTap}
            className="text-left mt-0.5 text-xs text-[var(--rs-text-2)] hover:text-[var(--rs-text)] transition-colors cursor-default select-none"
            aria-label={`Version ${APP_VERSION}`}
          >
            <span className="text-[var(--rs-text-3)]">Version:</span> {APP_VERSION}
          </button>
          <div className="mt-2">Deterministic calculation engine. No AI involved in the math.</div>
        </div>
      </section>

      {/* Developer Options — unlocked after 5 taps on the version number */}
      {devOptionsOpen && (
        <section
          className="card-rs p-4"
          data-testid="settings-dev-options"
          style={{ borderColor: "rgba(148,163,184,0.4)" }}
        >
          <div className="label-up text-[var(--rs-text-3)] mb-3 inline-flex items-center gap-2">
            <RefreshCw className="w-3.5 h-3.5" /> Developer Options
          </div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="text-xs font-semibold inline-flex items-center gap-1.5">
                {simOffline ? (
                  <WifiOff className="w-3.5 h-3.5" style={{ color: "var(--rs-amber)" }} />
                ) : (
                  <Wifi className="w-3.5 h-3.5" style={{ color: "rgb(74,222,128)" }} />
                )}
                Simulate Offline
              </div>
              <p className="text-[11px] text-[var(--rs-text-3)] mt-0.5 leading-snug">
                Pretend the device is offline. Banner appears, indicator turns amber.
              </p>
            </div>
            <button
              type="button"
              data-testid="dev-simulate-offline-toggle"
              role="switch"
              aria-checked={simOffline}
              onClick={onToggleSimOffline}
              className="relative h-6 w-11 rounded-full transition-colors shrink-0"
              style={{
                background: simOffline
                  ? "var(--rs-amber)"
                  : "rgba(148,163,184,0.35)",
              }}
            >
              <span
                className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform"
                style={{ transform: simOffline ? "translateX(20px)" : "translateX(0)" }}
              />
            </button>
          </div>
          <div className="text-[11px] text-[var(--rs-text-3)] mb-3 leading-snug">
            Live state: <span style={{ color: conn.online ? "rgb(74,222,128)" : "var(--rs-amber)" }}>
              {conn.online ? "online" : `offline (${conn.reason})`}
            </span>
          </div>
          <button
            data-testid="dev-force-reload-sw"
            onClick={onForceReloadSW}
            className="btn-ghost w-full h-10 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up"
            title="Evicts every PWA cache and re-installs the service worker. Saved sheets / wells / reports are preserved."
          >
            <RefreshCw className="w-3.5 h-3.5" /> Clear Cache &amp; Reload
          </button>

          {/* Storage warning simulator — writes dummy data under rsc.devSim.bloat.* */}
          <div className="mt-3 pt-3 border-t" style={{ borderColor: "rgba(148,163,184,0.2)" }}>
            <button
              data-testid="dev-simulate-storage-warning"
              onClick={onSimulateStorageWarning}
              className="btn-ghost w-full h-10 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up"
              style={{ borderColor: "rgba(255,179,0,0.45)", color: "var(--rs-amber)" }}
            >
              <AlertTriangle className="w-3.5 h-3.5" /> Simulate 4MB Storage Warning
            </button>
            <button
              data-testid="dev-clear-simulated-data"
              onClick={onClearSimulatedData}
              className="btn-ghost w-full h-10 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up mt-2"
            >
              <Trash2 className="w-3.5 h-3.5" /> Clear Simulated Data
            </button>
            <p className="text-[11px] text-[var(--rs-text-3)] mt-2 leading-snug">
              Simulator writes dummy records under a dedicated prefix
              (<span className="font-mono-rs">rsc.devSim.bloat.*</span>) so cleanup is surgical.
              Your saved sheets, settings, and favorites are not touched.
            </p>
          </div>

          <p
            className="text-[11px] text-[var(--rs-text-3)] mt-3 leading-snug"
            data-testid="dev-options-disclaimer"
          >
            These settings only affect your local browser. No data is sent to the server.
          </p>
        </section>
      )}

      <SafetyBanner />
    </div>
  );
}

// =========================================================================
// Default Planning Factors card (R2c). 9 user-editable fields with tooltips.
// Values are display-only — never applied to calculation outputs.
// =========================================================================
const PLANNING_FACTOR_SPEC = [
  { key: "maasp_safety_factor", label: "MAASP planning factor", type: "number", step: "0.01", min: 0, max: 1, tooltip: "Multiplier applied to the calculated MAASP planning value at the user's discretion. Not a regulatory or OEM-certified factor. User-adjustable planning aid — not a safety certification." },
  { key: "lot_fracture_safety_factor", label: "LOT/FIT planning factor", type: "number", step: "0.01", min: 0, max: 1, tooltip: "Multiplier applied to the LOT/FIT fracture value at the user's discretion. User-adjustable planning aid — not a safety certification." },
  { key: "trip_tank_displacement_rule", label: "Trip-tank displacement rule", type: "text", tooltip: "Reference rule of thumb for trip-tank displacement. Verify against the rig's standing instructions. User-adjustable planning aid — not a safety certification." },
  { key: "max_shut_in_casing_pressure_factor", label: "Shut-in casing pressure factor", type: "number", step: "0.01", min: 0, max: 1, tooltip: "Planning fraction of casing burst rating commonly used as a shut-in threshold. Verify against the casing tally and well program. User-adjustable planning aid — not a safety certification." },
  { key: "pit_gain_alarm_bbl", label: "Pit-gain alarm (bbl)", type: "number", step: "0.1", min: 0, tooltip: "Pit-gain alarm threshold in barrels. Set per rig procedure. User-adjustable planning aid — not a safety certification." },
  { key: "warning_threshold_percent", label: "Warning threshold (%)", type: "number", step: "1", min: 0, max: 100, tooltip: "Threshold at which planning-aid status indicators turn amber. Visual only. User-adjustable planning aid — not a safety certification." },
  { key: "critical_threshold_percent", label: "Critical threshold (%)", type: "number", step: "1", min: 0, max: 100, tooltip: "Threshold at which planning-aid status indicators turn red. Visual only. User-adjustable planning aid — not a safety certification." },
  { key: "stale_data_warning_days_mud", label: "Stale mud-data warning (days)", type: "number", step: "1", min: 0, tooltip: "Days after which the in-app reminder marks a stored mud reading as needing refresh. User-adjustable planning aid — not a safety certification." },
  { key: "stale_data_warning_days_casing", label: "Stale casing-data warning (days)", type: "number", step: "1", min: 0, tooltip: "Days after which the in-app reminder marks a stored casing inspection as needing refresh. User-adjustable planning aid — not a safety certification." },
];

function PlanningFactorsCard() {
  const [values, setValues] = useState(() => getPlanningFactors());
  const onChange = (key, raw) => {
    const spec = PLANNING_FACTOR_SPEC.find((p) => p.key === key);
    const v = spec.type === "number" ? (raw === "" ? "" : Number(raw)) : raw;
    setPlanningFactor(key, v);
    setValues(getPlanningFactors());
  };
  const onReset = (key) => {
    resetPlanningFactor(key);
    setValues(getPlanningFactors());
    toast.success(`Reset ${PLANNING_FACTOR_SPEC.find((p) => p.key === key).label} to factory default.`);
  };
  const onResetAll = () => {
    resetAllPlanningFactors();
    setValues(getPlanningFactors());
    toast.success("All planning factors reset to factory defaults.");
  };

  return (
    <section className="card-rs p-4" data-testid="settings-planning-factors">
      <div className="label-up text-[var(--rs-text-3)] mb-1 inline-flex items-center gap-2">
        <ShieldAlert className="w-3.5 h-3.5" /> Default Planning Factors
      </div>
      <p
        className="text-[11px] text-[var(--rs-text-3)] leading-snug mb-3"
        data-testid="planning-factors-eyebrow"
      >
        User-adjustable planning aids — not safety certifications.
      </p>
      <div className="flex flex-col gap-3">
        {PLANNING_FACTOR_SPEC.map((spec) => (
          <div key={spec.key} className="flex flex-col gap-1" data-testid={`planning-factor-row-${spec.key}`}>
            <label className="text-[11px] font-semibold text-[var(--rs-text-2)] flex items-center gap-1.5">
              {spec.label}
              <span
                className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border text-[9px] cursor-help"
                style={{ borderColor: "var(--rs-border)", color: "var(--rs-text-3)" }}
                title={spec.tooltip}
                data-testid={`planning-factor-tooltip-${spec.key}`}
              >i</span>
            </label>
            <div className="flex gap-2 items-center">
              <input
                data-testid={`planning-factor-input-${spec.key}`}
                type={spec.type}
                step={spec.step}
                min={spec.min}
                max={spec.max}
                value={values[spec.key] ?? ""}
                onChange={(e) => onChange(spec.key, e.target.value)}
                className="input-rs h-9 flex-1 text-[12px]"
              />
              <button
                type="button"
                data-testid={`planning-factor-reset-${spec.key}`}
                onClick={() => onReset(spec.key)}
                className="text-[10px] text-[var(--rs-text-3)] hover:text-[var(--rs-amber)] transition-colors px-2 label-up"
              >
                Reset
              </button>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        data-testid="planning-factors-reset-all"
        onClick={onResetAll}
        className="btn-ghost w-full h-10 rounded-md inline-flex items-center justify-center gap-2 text-xs label-up mt-3"
      >
        Reset all to defaults
      </button>
    </section>
  );
}
